import { useCallback, useState } from 'react';
import { SuggestedAddress } from '@blocs.address-suggestion';
import {
  AddressValidationInput,
  AddressValidationResult,
  AddressValidationVerdict,
} from '@minecraft.graphql-operations';
import { SignupAddressValidationEvents } from '../useSignupAddressValidationEvents';
import { BillingAddressFields, toAddressValidationInput } from './formatters';
import { useCheckBillingAddress } from './useCheckBillingAddress';

type UseAddressValidatorParams = {
  // Called once the modal resolves (Continue) with whatever address the user settled on. Write it
  // into your own form however you already do that — e.g. (address) =>
  // applySuggestedAddressToFormik(paymentMethodFormikRef.current, address). Fixed for the lifetime
  // of this hook instance, the same way useThreeDSecureFlow takes onAddPaymentOption/
  // onUpdatePaymentOption as stable params — there's exactly one form this hook is checking, so
  // there's never more than one thing "resolve" could mean.
  //
  // Optional: only needed when the caller of `checkAddress` doesn't already have direct access to
  // the Formik it's checking (e.g. a top-level submit handler that only has a ref). If the modal is
  // instead rendered by a component that already has that access via useFormikContext(), that
  // component can apply the correction directly in its own onSubmit and skip this entirely.
  onAddressCorrected?: (suggestedAddress: SuggestedAddress) => void;
  funnelEvents?: SignupAddressValidationEvents;
  // Fires once, the first time this hook instance ever flags an address (SUGGEST or REJECT) — lets a
  // caller remember "this checkout session hit address validation" past pendingAddressIssue resetting.
  onAddressIssueDetected?: () => void;
};

type PendingAddressIssue = {
  result: AddressValidationResult;
  currentAddress: BillingAddressFields;
  lastCheckedInput: AddressValidationInput;
};

// The Controller in this feature's Model/View/Controller split: wraps the stateless network check
// (useCheckBillingAddress, the Model source) and mediates with AddressValidationForm (the dumb,
// fully-controlled View) via a plain event contract. Mirrors useThreeDSecureFlow +
// <ThreeDSecureForm> — instantiate one of these per form that needs the gate, right next to that
// form; nothing here is threaded through unrelated files.
export const useAddressValidator = ({
  onAddressCorrected,
  funnelEvents,
  onAddressIssueDetected,
}: UseAddressValidatorParams) => {
  const checkBillingAddress = useCheckBillingAddress();
  const [pendingAddressIssue, setPendingAddressIssue] = useState<PendingAddressIssue>();

  // Resolves true (accepted — proceed) or false (rejected — the modal is now showing itself). No
  // onAccept callback: this path has no async gap to bridge, so the caller already has everything
  // it needs (its own formikHelpers/onToken) to act on this return value inline.
  const checkAddress = useCallback(
    async (fields: BillingAddressFields): Promise<boolean> => {
      const { result, wasCheckedByGoogle } = await checkBillingAddress(fields);
      if (wasCheckedByGoogle) {
        funnelEvents?.trackAddressValidationResponse({ verdict: result.verdict });
      }
      if (result.verdict === AddressValidationVerdict.Accept) {
        setPendingAddressIssue(undefined);
        return true;
      }
      funnelEvents?.trackAddressValidationModalShown({ verdict: result.verdict });
      onAddressIssueDetected?.();
      setPendingAddressIssue({ result, currentAddress: fields, lastCheckedInput: toAddressValidationInput(fields) });
      return false;
    },
    [checkBillingAddress, funnelEvents, onAddressIssueDetected]
  );

  // Wire directly as <AddressValidationForm onSubmit={resolveAddressIssue}>.
  const resolveAddressIssue = useCallback(
    (suggestedAddress: SuggestedAddress) => {
      setPendingAddressIssue(undefined);
      funnelEvents?.trackAddressValidationNextClicked();
      onAddressCorrected?.(suggestedAddress);
    },
    [onAddressCorrected, funnelEvents]
  );

  const dismissAddressIssue = useCallback(() => {
    funnelEvents?.trackAddressValidationExitClicked();
    setPendingAddressIssue(undefined);
  }, [funnelEvents]);

  return { checkAddress, pendingAddressIssue, resolveAddressIssue, dismissAddressIssue };
};

export type UseAddressValidator = ReturnType<typeof useAddressValidator>;
